/**
 * Raw fetcher functions untuk semua master data endpoints.
 *
 * Setiap fungsi:
 *   - Pure (terima params, return Promise<data>)
 *   - Tidak menyentuh Vue/Vuex/router
 *   - Dipanggil oleh composable di src/composables/master/*
 *
 * Response shape:
 *   GET .../admin (pagination=false) → { status, message, data: [...] }
 *   GET .../admin (pagination=true)  → { totalItems, currentPage, data: [...], ... }
 *
 * Composable yang memanggil sebaiknya mengembalikan `res.data` (array) untuk
 * pemakaian dropdown/select master. Jika butuh meta paginate, return raw response.
 */
import http from "@/config/http";

/* Branches */
export async function fetchBranches() {
  const { data } = await http.get("/api/v1/master/branch/admin", {
    params: { pagination: false },
  });
  return data;
}

/* Couriers */
export async function fetchCouriers() {
  const { data } = await http.get("/api/v1/shipping-rates/courier/admin", {
    params: { pagination: false },
  });
  return data;
}

/* Banks */
export async function fetchBanks() {
  const { data } = await http.get("/api/v1/master/bank/admin", {
    params: { pagination: false },
  });
  return data;
}

/* Bank-Courier mapping */
export async function fetchBankCouriers() {
  const { data } = await http.get("/api/v1/master/bank-courier/admin", {
    params: { pagination: false },
  });
  return data;
}

/* Payments (bank list yang dipakai sebagai opsi pembayaran) */
export async function fetchPayments() {
  /* Project ini memakai endpoint bank/admin sebagai source payment options */
  const { data } = await http.get("/api/v1/master/bank/admin", {
    params: { pagination: false },
  });
  return data;
}

/* Jobs */
export async function fetchJobs() {
  const { data } = await http.get("/api/v1/master/job/admin", {
    params: { pagination: false },
  });
  return data;
}

/* Roles */
export async function fetchRoles() {
  const { data } = await http.get("/api/v1/users/role", {
    params: { pagination: false },
  });
  return data;
}

/* Pickups */
export async function fetchPickups() {
  const { data } = await http.get("/api/v1/master/pickup", {
    params: { pagination: false },
  });
  return data;
}

/* Cost categories */
export async function fetchCostCategories() {
  const { data } = await http.get("/api/v1/master/cost-category", {
    params: { pagination: false },
  });
  return data;
}

/* Destinations (shipping rates) */
export async function fetchDestinations() {
  const { data } = await http.get("/api/v1/shipping-rates/destination", {
    params: { pagination: false },
  });
  return data;
}

/* Services (shipping rates) */
export async function fetchServices() {
  const { data } = await http.get("/api/v1/shipping-rates/service/admin", {
    params: { pagination: false },
  });
  return data;
}

/* Agens — paginate atau full. Default ambil paginate ringan; override via params. */
export async function fetchAgens(params = { pagination: true, page: 1, limit: 10 }) {
  const { data } = await http.get("/api/v1/users/user", { params });
  return data;
}

/* Agen Options — payload minimal (fullname/type_user/branch.city_name) untuk dropdown.
 * Default filter type_user=Agen. Set `type_user: null` untuk ambil semua user. */
export async function fetchAgenOptions(extra = {}) {
  const params = {
    pagination: "false",
    fields: "options",
    type_user: "Agen",
    ...extra,
  };
  if (params.type_user === null) delete params.type_user;
  const { data } = await http.get("/api/v1/users/user", { params });
  return data;
}

/* Members */
export async function fetchMembers(params = { pagination: true, page: 1, limit: 10 }) {
  const { data } = await http.get("/api/v1/users/member/get", { params });
  return data;
}

/* Page Setting (single record konfigurasi home/about/faq/contact) */
export async function fetchPageSetting() {
  const { data } = await http.get("/api/v1/master/page-setting/get");
  return data;
}

/* Check Ongkir — daftar provinsi (RajaOngkir) */
export async function fetchCheckOngkirProvinces() {
  const { data } = await http.get("/api/v1/shipping-rates/check-ongkir/province");
  return data;
}

/* Check Ongkir — daftar kota berdasarkan province_id (RajaOngkir) */
export async function fetchCheckOngkirCities(province) {
  const { data } = await http.get(
    `/api/v1/shipping-rates/check-ongkir/city/${province}`
  );
  return data;
}
